import React, { useEffect, useRef } from 'react';
import { motion, useDragControls, PanInfo } from 'framer-motion';
import { X, Minus, Maximize2, Minimize2 } from 'lucide-react';
import { useTheme } from '../../core/contexts/ThemeContext';
import { useWindows } from '../../core/contexts/WindowContext';
import { WindowState } from '../../core/types/window';
interface WindowProps {
  window: WindowState;
  children: React.ReactNode;
}
export function Window({
  window,
  children
}: WindowProps) {
  const {
    theme,
    getThemeClasses
  } = useTheme();
  const {
    windows,
    closeWindow,
    minimizeWindow,
    maximizeWindow,
    focusWindow,
    updateWindowPosition,
    isMobile
  } = useWindows();
  const themeClasses = getThemeClasses();
  const dragControls = useDragControls();
  const maxZIndex = Math.max(...windows.map(w => w.zIndex));
  const isActive = window.zIndex === maxZIndex;
  const isLightTheme = theme === 'light';
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [children]);
  // Mobile: fullscreen overlay
  if (isMobile) {
    return <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: window.isMinimized ? 0 : 1,
      y: window.isMinimized ? 20 : 0
    }} exit={{
      opacity: 0,
      y: 20
    }} transition={{
      type: 'spring',
      bounce: 0.15,
      duration: 0.4
    }} style={{
      display: window.isMinimized ? 'none' : 'flex',
      position: 'fixed',
      top: 40,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: window.zIndex,
      flexDirection: 'column',
      pointerEvents: window.isMinimized ? 'none' : 'auto'
    }} className={`${themeClasses.bg} ${themeClasses.text}`} data-id="element-239">
        <div className={`px-4 py-3.5 flex items-center justify-between border-b ${themeClasses.border}`} data-id="element-240">
          <h2 className="font-mono text-xs uppercase tracking-wider font-medium" data-id="element-241">
            {window.title}
          </h2>
          <button onClick={() => closeWindow(window.id)} className={`p-2 rounded-lg transition-colors ${isLightTheme ? 'hover:bg-black/5' : 'hover:bg-white/10'}`} data-id="element-242">
            <X className="w-4 h-4" data-id="element-243" />
          </button>
        </div>
        <div className="flex-1 overflow-auto pb-24" data-id="element-244">{children}</div>
      </motion.div>;
  }
  // Desktop: draggable, resizable window
  const handleDragEnd = (_: any, info: PanInfo) => {
    if (!window.isMaximized) {
      updateWindowPosition(window.id, {
        x: window.position.x + info.offset.x,
        y: window.position.y + info.offset.y
      });
    }
  };
  return <motion.div drag={!window.isMaximized} dragControls={dragControls} dragListener={false} dragMomentum={false} onDragEnd={handleDragEnd} initial={{
    opacity: 0,
    scale: 0.96,
    x: window.isMaximized ? 0 : window.position.x,
    y: window.isMaximized ? 0 : window.position.y
  }} animate={{
    opacity: window.isMinimized ? 0 : isActive ? 1 : 0.95,
    scale: window.isMinimized ? 0.96 : 1,
    x: window.isMaximized ? 0 : window.position.x,
    y: window.isMaximized ? 0 : window.position.y,
    width: window.isMaximized ? '100vw' : window.size.width,
    height: window.isMaximized ? '100vh' : window.size.height
  }} exit={{
    opacity: 0,
    scale: 0.96,
    y: 20
  }} transition={{
    type: 'spring',
    stiffness: 200,
    damping: 28
  }} style={{
    zIndex: window.zIndex,
    position: 'fixed',
    top: 0,
    left: 0,
    pointerEvents: window.isMinimized ? 'none' : 'auto'
  }} onClick={() => !isActive && focusWindow(window.id)} className={`
        ${themeClasses.card}
        rounded-2xl overflow-hidden
        ${isActive ? isLightTheme ? 'shadow-2xl shadow-black/[0.08]' : 'shadow-2xl shadow-black/40' : isLightTheme ? 'shadow-xl shadow-black/[0.04]' : 'shadow-xl shadow-black/20'}
        transition-shadow duration-300
      `} data-id="element-245">
      {/* Window Header */}
      <div onPointerDown={e => {
      if (!window.isMaximized) {
        focusWindow(window.id);
        dragControls.start(e);
      }
    }} className={`
          px-5 py-3.5 flex items-center justify-between
          select-none border-b ${themeClasses.border}
          ${window.isMaximized ? 'cursor-default' : 'cursor-move'}
        `} data-id="element-246">
        {/* Traffic Lights */}
        <div className="flex items-center gap-2.5" onClick={e => e.stopPropagation()} data-id="element-247">
          <button onClick={() => closeWindow(window.id)} className="w-2.5 h-2.5 rounded-full bg-[#FF5F56] hover:bg-[#FF5F56]/80 transition-colors flex items-center justify-center group" data-id="element-248">
            <X className="w-2 h-2 text-black/50 opacity-0 group-hover:opacity-100" data-id="element-249" />
          </button>
          <button onClick={() => minimizeWindow(window.id)} className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E] hover:bg-[#FFBD2E]/80 transition-colors flex items-center justify-center group" data-id="element-250">
            <Minus className="w-2 h-2 text-black/50 opacity-0 group-hover:opacity-100" data-id="element-251" />
          </button>
          <button onClick={() => maximizeWindow(window.id)} className="w-2.5 h-2.5 rounded-full bg-[#27C93F] hover:bg-[#27C93F]/80 transition-colors flex items-center justify-center group" data-id="element-252">
            {window.isMaximized ? <Minimize2 className="w-2 h-2 text-black/50 opacity-0 group-hover:opacity-100" data-id="element-253" /> : <Maximize2 className="w-2 h-2 text-black/50 opacity-0 group-hover:opacity-100" data-id="element-254" />}
          </button>
        </div>

        {/* Title */}
        <div className={`absolute left-1/2 -translate-x-1/2 font-mono text-xs uppercase tracking-wider font-medium pointer-events-none ${isLightTheme ? 'text-[#6B6B6B]' : 'text-[#9B9B9B]'}`} data-id="element-255">
          {window.title}
        </div>
      </div>

      {/* Window Content */}
      <div ref={contentRef} className="h-[calc(100%-45px)] overflow-auto bg-transparent relative z-0" data-id="element-256">
        {children}
      </div>
    </motion.div>;
}